import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { BillingConsequences, CreditPosition, PlanRow } from '../models/useBilling'
import { currentPeriod, formatPeriod, periodBounds, type ComputeUsage as ComputeRow } from '../models/useUsage'
import { formatCents } from '../utilities/general'
import { resetLabel } from '../utilities/freshness'
import { AXIS_PALETTE } from '../utilities/usageAxis'
import {
  apportionRowCents,
  computeClassLabel,
  computeSplit,
  computeStopWarning,
  computeTotals,
  computeUpsellLine,
  computeUpsellPlan,
  formatCostCents,
  formatDuration,
  freeComputeBudget,
  hasComputeUsage,
  includedLabel,
  isClimbing,
  machineTimeLeftHint,
  referenceComputeClass,
  showComputeCard,
  usedComputeRows,
  type ComputeSplit,
} from '../utilities/computeUsage'

/**
 * The compute usage view (app#136 item 3, app#327).
 *
 * ⚠⚠ MONEY, NOT HOURS (Benoit, 2026-09-14). The allowance is one dollar pool shared by every
 * machine size. When this panel printed hours, it printed SMALL-machine hours, so one medium hour
 * read as 3 h beside a "Ran" column that said 1 h. Every figure here is dollars now, and time is
 * only the real machine time of a row, as `h m`.
 *
 * ⚠⚠ TWO FRAMINGS, AND THEY MUST STAY DIFFERENT.
 * - PAID: the headline is what was used. The included amount is a labelled SEGMENT of the bar,
 *   never its end, and past it the bar continues as "from credit". Never "limit" or "maximum": a
 *   Pro customer who reads "$20 buys at most $5 of compute" has read something false.
 * - FREE (`hard_caps`): a monthly budget. "$0.13 of $1.00 free compute used · $0.87 left".
 *
 * ⚠⚠ ONE FIGURE FOR ONE QUANTITY. The headline, the split and "left" start from the compute
 * axis's `used`, the exact cents the bar above prints, and the part "from credit" is the axis's
 * `overage_cents`, the figure the line under that bar prints. The rows are whole cents apportioned
 * from the same exact sum (`apportionRowCents`), so they add up to the Total row, and the Total
 * row equals the bar.
 *
 * ⚠⚠ UNPRICED IS NOT ZERO. An off-catalogue shape has no rate, and `docs/PRICING.md` § 4.2
 * requires it to render as unpriced. "$0.00" would tell a customer we ran their task for free.
 *
 * ⚠ A row with OPEN tasks is a FLOOR and says so.
 *
 * ⚠⚠ AN EMPTY MONTH STILL RENDERS THE CARD (Benoit, 2026-09-14) — see `showComputeCard`. A Free
 * org reads "$0.00 of $5.50 free compute used", the promotion included exactly as in a month with
 * usage. Only the TABLE is left out: the RPC returns every offered class at zero, and a table of
 * zeroes is a worse answer than one sentence. The card renders nothing only when there is no
 * pool to show (exempt tier, not loaded, or $0 included) and nothing ran.
 */
export default function ComputeUsage({
  rows,
  error,
  period,
  usedCents,
  includedCents,
  overageCents,
  isFree,
  isPaid,
  planName,
  plan,
  plans,
  credit,
  consequences,
}: Readonly<{
  rows: ComputeRow[]
  /** The breakdown read failed (`useUsage.computeError`): the rows are not an empty month. */
  error: boolean
  /** The period on screen, `YYYY-MM`. */
  period: string
  /** The compute axis's `used` from `get_org_usage`, exact cents — the figure the bar above
   *  prints. Undefined (axis not loaded) falls back to the exact sum of the rows. */
  usedCents: number | undefined
  /** The compute axis's `included` from `get_org_usage` — the same figure the bar above draws.
   *  Null is the exempt tier (no pool); undefined is not loaded. */
  includedCents: number | null | undefined
  /** The compute axis's `overage_cents` — the "from credit" figure under the bar above. */
  overageCents: number | undefined
  isFree: boolean
  isPaid: boolean
  planName: string | null
  plan: PlanRow | null
  plans: readonly PlanRow[]
  credit: CreditPosition | null
  consequences: BillingConsequences | null
}>) {
  const theme = useTheme()
  if (error || !showComputeCard(rows, includedCents)) return null

  const ran = hasComputeUsage(rows)
  const used = usedComputeRows(rows)
  const rowCents = apportionRowCents(used)
  const totals = computeTotals(used)
  // ⚠⚠ The bar's figure, not the rows': see the note on this component.
  const usedExact = usedCents ?? totals.exactCents
  // ⚠ "From credit" is the bar's `overage_cents` on every plan that is not Free. Free has no
  // second segment, so its split keeps the plain difference.
  const split = computeSplit(usedExact, includedCents, isFree ? undefined : overageCents)
  const budget = isFree && split ? freeComputeBudget(split, usedExact) : null
  const isCurrent = period === currentPeriod()
  const resets = resetLabel(period, currentPeriod(), periodBounds(period).end)
  const month = formatPeriod(period)
  const usedText = formatCostCents(Math.round(usedExact), usedExact)
  const color = theme.palette[AXIS_PALETTE.compute].main

  const reference = referenceComputeClass(rows)
  // ⚠ The upsell and the credit balance are facts about NOW, so a past month shows neither.
  const upsell = isFree && isCurrent ? computeUpsellPlan(plans) : null
  const stops = computeStopWarning({ isPaid, isCurrent, split, credit, consequences })
  const balance =
    isPaid && isCurrent && credit?.creditBilled && credit.balanceCents !== null ? credit.balanceCents : null

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
          <Typography variant="subtitle2" component="h3">
            Compute
          </Typography>
          {totals.climbing && <Chip size="small" label="still running" variant="outlined" color="warning" />}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {month}
        </Typography>
      </Stack>

      {budget ? (
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {budget.headline}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {budget.left}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {usedText} used {isCurrent ? 'this month' : `in ${month}`}
        </Typography>
      )}

      {split && <SplitBar split={split} color={color} free={isFree} />}

      {/* The legend for the two segments. Free has one segment and its headline says it all. */}
      {split && !isFree && (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mb: 0.5 }}>
          <LegendItem swatch={color}>
            {includedLabel(split.includedCents, plan, planName)}
            {split.pastIncludedCents === 0 && ` · ${formatCents(split.leftCents)} left`}
          </LegendItem>
          {split.pastIncludedCents > 0 && (
            <LegendItem swatch={alpha(color, 0.4)}>
              {/* ⚠ "from credit" only on a plan that pays from credit. A plan that has not
                  loaded is neither Free nor paid, and must not name a control it may not have. */}
              {formatCents(split.pastIncludedCents)} {isPaid ? 'from credit' : 'past the included amount'}
            </LegendItem>
          )}
        </Stack>
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {[
          resets,
          isFree && split && isCurrent ? machineTimeLeftHint(split.leftCents, reference) : '',
          balance !== null ? `credit balance ${formatCents(balance)}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>

      {upsell && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {/* ⚠ The plan's amount is named only when it is more than this budget: `computeUpsellLine`. */}
          Need more? {computeUpsellLine(upsell, includedCents)}{' '}
          <Link component={RouterLink} to="/billing" variant="caption">
            See plans
          </Link>
        </Typography>
      )}

      {/* ⚠⚠ THE ONE CASE WHERE A PAID PLAN STOPS, SAID PLAINLY. See `computeStopsWithoutCredit`. */}
      {stops && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          action={
            <Button component={RouterLink} to="/billing" color="inherit" size="small">
              Billing controls
            </Button>
          }
        >
          {/* ⚠ The remedy follows the cause. With paying from credit off, a positive balance
              still stops machines, so "add credit" would name a remedy that does not work. */}
          {stops === 'no-credit-spending'
            ? 'Included compute used — machines stop until you turn on paying from credit.'
            : 'Included compute used — machines stop until you add credit or raise your cap.'}
        </Alert>
      )}

      {/* ⚠ An empty month shows the budget above and one sentence here, never a table of zeroes. */}
      {!ran && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          No compute ran {isCurrent ? 'this month' : `in ${month}`}.
        </Typography>
      )}

      {/* ⚠ A TABLE IN A DIALOG NEEDS ITS OWN HORIZONTAL SCROLL — `TableContainer` is that scroll.
          At phone width this panel's paper is about 310 px and the columns want more. */}
      {ran && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Size</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Time used</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {used.map((r, i) => {
                const cents = rowCents[i]
                return (
                  <TableRow key={`${r.class ?? 'off'}-${r.cpu}-${r.memoryMb}`}>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <span>{computeClassLabel(r)}</span>
                        {isClimbing(r) && <Chip size="small" variant="outlined" label={`${r.openTasks} running`} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {r.priceCentsPerHour !== null ? `${formatCents(r.priceCentsPerHour)}/h` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(r.billedMinutes)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {/* ⚠ Narrowed on the VALUE: the compiler refusing `formatCents(null)` here is the
                          check that keeps "$0.00" off an unpriced row. */}
                      {cents !== null ? (
                        formatCostCents(cents, r.costCents ?? 0)
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          unpriced
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                <TableCell />
                <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(totals.minutes)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {/* ⚠ The sum of the printed rows, which is `Math.round` of their exact sum. */}
                  {formatCostCents(totals.costCents, totals.exactCents)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {ran && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Billed per minute.
            {totals.climbing && ' Machines are still running, so these figures will grow.'}
            {/* ⚠ The unpriced count is stated rather than folded into the total, which would
                silently under-state what ran. */}
            {totals.unpricedRows > 0 &&
              ` ${totals.unpricedRows} row${totals.unpricedRows > 1 ? 's are' : ' is'} unpriced (a size from before the current catalogue — it bills nothing).`}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

function LegendItem({ swatch, children }: Readonly<{ swatch: string; children: ReactNode }>) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: swatch, flexShrink: 0 }} />
      <Typography variant="caption">{children}</Typography>
    </Stack>
  )
}

/**
 * One bar in two segments: the part the plan included, then the part past it.
 *
 * ⚠⚠ THE BAR'S END IS WHAT WAS USED, NOT THE INCLUDED AMOUNT, ON A PAID PLAN. The scale is the
 * larger of the two, so past the included amount the bar keeps growing in a second tone instead
 * of pinning at 100% in red, which reads as a ceiling. Free is a budget: the track is the included
 * amount and the fill is what was used.
 */
function SplitBar({ split, color, free }: Readonly<{ split: ComputeSplit; color: string; free: boolean }>) {
  const theme = useTheme()
  const scale = Math.max(split.usedCents, split.includedCents, 1)
  const pct = (cents: number) => `${(cents / scale) * 100}%`
  return (
    <Box
      role="img"
      aria-label={
        free
          ? `${formatCents(split.usedCents)} of ${formatCents(split.includedCents)} used`
          : `${formatCents(split.includedUsedCents)} of the included amount used, ${formatCents(split.pastIncludedCents)} past it`
      }
      sx={{
        display: 'flex',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: theme.palette.action.hover,
        my: 1,
      }}
    >
      <Box sx={{ width: pct(split.includedUsedCents), bgcolor: color }} />
      {!free && split.pastIncludedCents > 0 && (
        <Box
          sx={{
            width: pct(split.pastIncludedCents),
            bgcolor: alpha(color, 0.4),
            borderLeft: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      )}
    </Box>
  )
}
